// imu.ts — phone IMU streaming for visual-inertial fusion (ARCHITECTURE.md
// §9, core/imu). DeviceMotionEvent gives `rotationRate` in deg/s (alpha about
// device z, beta about x, gamma about y) and `accelerationIncludingGravity` in
// m/s², both in device axes. Samples are converted to imu.Sample (rad/s, x/y/z
// order), timestamped on the performance.now() clock shared with frame
// timestamps, and flushed in ~50 ms batches together with the screen
// orientation angle. iOS needs DeviceMotionEvent.requestPermission() inside
// the Start tap; anything short of a working sensor falls back silently to
// vision-only tracking.

import type { ImuSampleJSON } from './worker';

export const IMU_BATCH_MS = 50;
export const IMU_FUSED_MIN_SAMPLES = 10;
const DEG = Math.PI / 180;

export interface MotionLike {
  rotationRate: { alpha: number | null; beta: number | null; gamma: number | null } | null;
  accelerationIncludingGravity: { x: number | null; y: number | null; z: number | null } | null;
}

// convertMotion maps one DeviceMotionEvent to an imu.Sample, or null when the
// event carries no usable gyro + accelerometer data.
export function convertMotion(e: MotionLike, tMs: number): ImuSampleJSON | null {
  const r = e.rotationRate;
  const a = e.accelerationIncludingGravity;
  if (!r || !a) return null;
  if (r.alpha == null || r.beta == null || r.gamma == null) return null;
  if (a.x == null || a.y == null || a.z == null) return null;
  const g: [number, number, number] = [r.beta * DEG, r.gamma * DEG, r.alpha * DEG];
  const acc: [number, number, number] = [a.x, a.y, a.z];
  if (![...g, ...acc].every(Number.isFinite)) return null;
  return { t: tMs, g, a: acc };
}

// MAX_EVENT_AGE_MS bounds how far an event's timeStamp may trail the
// handler before it is not trusted as a performance.now() time.
export const MAX_EVENT_AGE_MS = 1000;

// sampleTime picks a sample's time: the event's own timeStamp (when the
// sensor reading was dispatched, on the performance.now() clock) rather
// than when the handler ran, which lags by however long the main thread was
// busy. The joint trajectory solver fits IMU against frame times, so that
// jitter matters. Browsers that stamp events on another clock (epoch ms, or
// 0) fall back to now.
export function sampleTime(eventTs: number, now: number): number {
  if (Number.isFinite(eventTs) && eventTs > 0 && eventTs <= now && now - eventTs <= MAX_EVENT_AGE_MS) return eventTs;
  return now;
}

// screenAngle returns the current screen rotation in degrees (0/90/180/270).
export function screenAngle(): number {
  const so = typeof screen !== 'undefined' ? screen.orientation : undefined;
  if (so && typeof so.angle === 'number') return so.angle;
  const legacy = (window as unknown as { orientation?: number }).orientation;
  return typeof legacy === 'number' ? ((legacy % 360) + 360) % 360 : 0;
}

type PermissionFn = () => Promise<'granted' | 'denied' | 'default'>;

export function imuSupported(): boolean {
  return typeof window !== 'undefined' && 'DeviceMotionEvent' in window;
}

// requestImuPermission must be called synchronously from a user gesture
// handler (before any await) for iOS to show its prompt. Resolves true when
// motion events may flow.
export function requestImuPermission(): Promise<boolean> {
  if (!imuSupported()) return Promise.resolve(false);
  const req = (DeviceMotionEvent as unknown as { requestPermission?: PermissionFn }).requestPermission;
  if (typeof req !== 'function') return Promise.resolve(true); // Android / desktop: no prompt
  return req()
    .then((state) => {
      console.info(`[openscan/imu] permission ${state}`);
      return state === 'granted';
    })
    .catch((err: unknown) => {
      console.info('[openscan/imu] permission request failed; vision-only', err);
      return false;
    });
}

export class ImuStream {
  private buf: ImuSampleJSON[] = [];
  private timer: number | undefined;
  private total = 0;
  private running = false;

  constructor(private onBatch: (samples: ImuSampleJSON[], screenAngleDeg: number) => void) {}

  // fused reports whether real sensor data is flowing.
  get fused(): boolean {
    return this.total >= IMU_FUSED_MIN_SAMPLES;
  }

  get sampleCount(): number {
    return this.total;
  }

  start(): void {
    if (this.running || !imuSupported()) return;
    this.running = true;
    window.addEventListener('devicemotion', this.handleMotion);
    this.timer = window.setInterval(this.flush, IMU_BATCH_MS);
    console.info('[openscan/imu] streaming started');
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    window.removeEventListener('devicemotion', this.handleMotion);
    window.clearInterval(this.timer);
    this.flush();
    console.info(`[openscan/imu] streaming stopped after ${this.total} samples`);
  }

  private handleMotion = (e: DeviceMotionEvent): void => {
    const s = convertMotion(e, sampleTime(e.timeStamp, performance.now()));
    if (!s) return;
    this.buf.push(s);
    this.total++;
    if (this.total === IMU_FUSED_MIN_SAMPLES) console.info('[openscan/imu] sensor data flowing; IMU fused');
  };

  private flush = (): void => {
    if (this.buf.length === 0) return;
    const batch = this.buf;
    this.buf = [];
    this.onBatch(batch, screenAngle());
  };
}
